import React, { useCallback, useState } from 'react';
import { Popover, Switch, Button, Typography, Divider, Alert } from 'antd';
import { X, Clock, FileVideo, AlertTriangle } from 'lucide-react';
import { useAtom, useAtomValue } from 'jotai';
import {
  isDadanExportPopoverOpenAtom,
  dadanExportChaptersEnabledAtom,
  dadanExportMetricsAtom,
  dadanExportStatusAtom,
  canStartDadanExportAtom,
  dadanExportProjectIdAtom,
  isDadanExportInProgressAtom,
  projectDurationLimitAtom,
} from '../../atoms/exportAtoms';
import { activeProjectAtom, sceneListAtom } from '../../atoms/sceneAtoms';
import ExportProgress from './ExportProgress';
import ExportSuccess from './ExportSuccess';
import { useDadanExport } from '../../hooks/useDadanExport';
import { useFeatureAccess } from '../../hooks/useFeatureAccess';
import { UpgradePrompt } from '../overlays/UpgradePrompt';
import './export.scss';

const DEFAULT_FREE_LIMIT_MINUTES = 5;
const DURATION_GRACE_PERIOD_MS = 2000; // 2 second grace period for auto-stop timing delays

const { Text, Title } = Typography;

const formatDuration = (ms: number): string => {
  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  
  if (hours > 0) {
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }
  return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
};

interface ExportPopoverProps {
  children: React.ReactNode;
}

const ExportPopover: React.FC<ExportPopoverProps> = ({ children }) => {
  const [isOpen, setIsOpen] = useAtom(isDadanExportPopoverOpenAtom);
  const [exportProjectId, setExportProjectId] = useAtom(dadanExportProjectIdAtom);
  const [chaptersEnabled, setChaptersEnabled] = useAtom(dadanExportChaptersEnabledAtom);
  const metrics = useAtomValue(dadanExportMetricsAtom);
  const status = useAtomValue(dadanExportStatusAtom);
  const canExport = useAtomValue(canStartDadanExportAtom);
  const isExportInProgress = useAtomValue(isDadanExportInProgressAtom);
  const durationLimit = useAtomValue(projectDurationLimitAtom);
  const project = useAtomValue(activeProjectAtom);
  const scenes = useAtomValue(sceneListAtom);
  
  const { startExport, retryExport, isStarting } = useDadanExport();
  const { getLimit, isPaid } = useFeatureAccess();
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
  
  // Check if free user exceeds their duration limit
  const getUserDurationLimit = (): number => {
    const durationLimit = getLimit("DURATION_PER_VIDEO");
    if (durationLimit !== null) {
      return durationLimit * 60 * 1000; // Convert minutes to ms
    }
    // Default for free users
    return isPaid ? Infinity : DEFAULT_FREE_LIMIT_MINUTES * 60 * 1000;
  };
  
  const userDurationLimitMs = getUserDurationLimit();
  // Add grace period for auto-stop timing delays (same as confirm scene logic)
  const effectiveLimitMs = userDurationLimitMs + DURATION_GRACE_PERIOD_MS;
  const exceedsUserLimit = !isPaid && metrics.totalDuration > effectiveLimitMs;
  
  // BULLETPROOF: Only show popover if exportProjectId EXPLICITLY matches current project
  // Treat exportProjectId === null as "closed" to prevent flash during atom reset race
  const currentProjectId = project?.id ? Number(project.id) : null;
  const shouldShowPopover = isOpen && exportProjectId !== null && exportProjectId === currentProjectId;

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, [setIsOpen]);

  // Control popover open/close - prevent accidental closing during active export
  const handleOpenChange = useCallback((open: boolean) => {
    // When opening, set BOTH isOpen and exportProjectId atomically
    // This ensures shouldShowPopover passes only for the intended project
    if (open) {
      if (currentProjectId !== null) {
        setExportProjectId(currentProjectId);
        setIsOpen(true);
      }
      return;
    }
    // During active export, only close via explicit close button (handleClose)
    // This prevents accidental dismissal by clicking outside
    if (!isExportInProgress) {
      setIsOpen(false);
    }
  }, [currentProjectId, isExportInProgress, setIsOpen, setExportProjectId]);

  const handleExport = useCallback(async () => {
    if (!project) return;
    
    // Gate export for free users exceeding duration limit
    if (exceedsUserLimit) {
      setShowUpgradePrompt(true);
      return;
    }
    
    await startExport();
  }, [project, startExport, exceedsUserLimit]);

  const getFirstRecordedSceneThumbnail = () => {
    const recordedScene = scenes.find(s => s.status === 'recorded');
    return recordedScene?.thumbnail || recordedScene?.thumbnailUrl || null;
  };

  const renderIdleContent = () => (
    <div className="export-popover__content">
      <div className="export-popover__header">
        <Title level={5} className="export-popover__title">Export</Title>
        <Button
          type="text"
          icon={<X size={16} />}
          onClick={handleClose}
          className="export-popover__close"
        />
      </div>
      
      <Divider className="export-popover__divider" />
      
      <div className="export-popover__preview">
        <div className="export-popover__thumbnail">
          {getFirstRecordedSceneThumbnail() ? (
            <img src={getFirstRecordedSceneThumbnail()!} alt="Preview" />
          ) : (
            <div className="export-popover__thumbnail-placeholder">
              <FileVideo size={24} />
            </div>
          )}
        </div>
        <div className="export-popover__meta">
          <div className="export-popover__meta-item">
            <Clock size={14} />
            <Text>Duration: {formatDuration(metrics.totalDuration)}</Text>
          </div>
          <div className="export-popover__meta-item">
            <FileVideo size={14} />
            <Text>Est. Size: ~{metrics.estimatedSize} MB</Text>
          </div>
        </div>
      </div>
      
      <Divider className="export-popover__divider" />
      
      <div className="export-popover__options">
        <div className="export-popover__option">
          <div className="export-popover__option-label">
            <Switch
              checked={chaptersEnabled}
              onChange={setChaptersEnabled}
              size="small"
            />
            <Text>Add Chapters to Video</Text>
          </div>
          <Text type="secondary" className="export-popover__option-help">
            Scenes will be marked as chapters for easy navigation
          </Text>
        </div>
      </div>
      
      <Divider className="export-popover__divider" />
      
      {durationLimit.exceedsLimit && (
        <Alert
          type="warning"
          showIcon
          icon={<AlertTriangle size={16} />}
          message="Duration Limit Exceeded"
          description={durationLimit.reason}
          style={{ marginBottom: 16 }}
        />
      )}
      
      <Button
        type="primary"
        block
        size="large"
        onClick={handleExport}
        loading={isStarting}
        disabled={!canExport}
        className="export-popover__submit"
      >
        Export Video
      </Button>
    </div>
  );

  const renderContent = () => {
    switch (status) {
      case 'idle':
        return renderIdleContent();
      case 'preparing':
      case 'queued':
      case 'stitching':
      case 'uploading':
        return <ExportProgress />;
      case 'complete':
        return <ExportSuccess onClose={handleClose} />;
      case 'failed':
        return <ExportProgress onRetry={retryExport} isRetrying={isStarting} />;
      default:
        return renderIdleContent();
    }
  };

  const limitMinutes = Math.round(userDurationLimitMs / 60000);

  return (
    <>
      <Popover
        open={shouldShowPopover}
        onOpenChange={handleOpenChange}
        content={renderContent()}
        trigger="click"
        placement="bottom"
        overlayClassName="export-popover"
        destroyOnHidden
      >
        {children}
      </Popover>
      
      <UpgradePrompt
        visible={showUpgradePrompt}
        onClose={() => setShowUpgradePrompt(false)}
        feature="Extended Video Duration"
        description={`Your project duration (${formatDuration(metrics.totalDuration)}) exceeds the ${limitMinutes}-minute limit for free users. Upgrade to Pro for unlimited video duration and export longer projects.`}
      />
    </>
  );
};

export default ExportPopover;
